/**
 * Convert Loops-format HTML (with {{tags}}) → TipTap-parseable HTML.
 * Uses DOMParser so {{tags}} inside href/src attributes are NOT transformed.
 */
export function htmlToTiptap(html) {
    if (!html) return '<p></p>'

    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')

    // Split <br> inside <p> tags into separate <p> tags so TipTap sees individual paragraphs.
    // This makes list toggling work naturally (each line becomes a <li>).
    // Preserves <br> inside <li>, <blockquote>, etc.
    const paragraphs = doc.body.querySelectorAll('p')
    for (const p of paragraphs) {
        const brs = p.querySelectorAll('br')
        if (brs.length === 0) continue

        // Split this <p> at each <br> into multiple <p> tags
        const fragment = doc.createDocumentFragment()
        let currentP = doc.createElement('p')
        // Copy attributes from original <p> (e.g., style)
        for (const attr of p.attributes) {
            currentP.setAttribute(attr.name, attr.value)
        }

        for (const child of Array.from(p.childNodes)) {
            if (child.nodeName === 'BR') {
                // Flush current paragraph (even if empty — preserves blank lines)
                fragment.appendChild(currentP)
                currentP = doc.createElement('p')
                for (const attr of p.attributes) {
                    currentP.setAttribute(attr.name, attr.value)
                }
            } else {
                currentP.appendChild(child.cloneNode(true))
            }
        }
        // Always append the last paragraph (even if empty — trailing blank lines)
        fragment.appendChild(currentP)

        p.parentNode.replaceChild(fragment, p)
    }

    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, null)
    const textNodes = []
    let node
    while ((node = walker.nextNode())) textNodes.push(node)

    const TAG_RE = /\{\{(\w+)\}\}/g

    for (const textNode of textNodes) {
        const text = textNode.textContent
        if (!text.includes('{{')) continue

        const frag = doc.createDocumentFragment()
        let lastIndex = 0
        let match
        TAG_RE.lastIndex = 0

        while ((match = TAG_RE.exec(text)) !== null) {
            if (match.index > lastIndex) {
                frag.appendChild(doc.createTextNode(text.slice(lastIndex, match.index)))
            }
            const span = doc.createElement('span')
            span.setAttribute('data-type', 'mergeTag')
            span.setAttribute('data-tag', match[1])
            span.textContent = match[0]
            frag.appendChild(span)
            lastIndex = TAG_RE.lastIndex
        }

        if (lastIndex < text.length) {
            frag.appendChild(doc.createTextNode(text.slice(lastIndex)))
        }

        textNode.parentNode.replaceChild(frag, textNode)
    }

    return doc.body.innerHTML
}

/**
 * Convert TipTap HTML → Loops-format HTML.
 */
export function tiptapToHtml(html) {
    if (!html) return ''
    // 1. Replace merge tag spans with {{tag}} syntax
    //    Attributes can appear in any order (TipTap may put tag="x" before data-type)
    let out = html.replace(
        /<span[^>]*data-type="mergeTag"[^>]*data-tag="([^"]*)"[^>]*>[^<]*<\/span>/g,
        '{{$1}}'
    )
    // 2. Normalize all empty paragraph variants to <p>&nbsp;</p>
    //    Catches: <p></p>, <p><br></p>, <p><br class="..."></p>, <p><br><br></p>, etc.
    out = out.replace(/<p([^>]*)>(\s*(<br[^>]*>)*\s*)<\/p>/gi, '<p$1>&nbsp;</p>')

    // 3. Collapse button-adjacent empty-p spacers. ProseMirror inserts gap
    //    paragraphs around atomic block nodes (buttonBlock) for cursor
    //    placement; those stack with user-authored Enter presses and produce
    //    visibly more space in the preview/sent email than the editor shows.
    //    Keep one empty-p on each side (user's intended blank line) and strip
    //    the extras. Only applies immediately adjacent to buttonBlock — does
    //    NOT affect multi-blank-line spacing elsewhere in the document.
    out = out.replace(/(<p[^>]*>(?:&nbsp;|\u00a0)<\/p>\s*){2,}(?=<div[^>]*data-type="buttonBlock")/gi, '<p>&nbsp;</p>')
    out = out.replace(/(<div[^>]*data-type="buttonBlock"[^>]*>[\s\S]*?<\/div>\s*)(<p[^>]*>(?:&nbsp;|\u00a0)<\/p>\s*){2,}/gi, '$1<p>&nbsp;</p>')

    return out
}
